// M70c (D8/E2/E10) — the offline shot queue, `qs.pending.shots`. An entry is
// only written AFTER confirm-before-enqueue decided the write did NOT land
// (or the device is plainly offline). Replay (AppShell) re-runs the lane
// logic through the same confirm check; entries that keep failing surface in
// the pending-logs sheet (Retry / Discard) — never a silent forever-requeue.
// Mirrors tripTaps' defensive read style.

export interface PendingShot {
  /** Local id for Retry/Discard addressing (not a DB id). */
  id: string;
  /** Local calendar day the shots happened ('YYYY-MM-DD'). */
  date: string;
  magazine_id: string;
  rounds: number;
  /** Venue for a session the replay may have to create (E10). */
  venue_id: string | null;
  /** Known session at enqueue time — replay targets it instead of resolving. */
  session_id?: string;
}

const KEY = 'qs.pending.shots';

function makeId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `ps-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
  }
}

export function readPendingShots(): PendingShot[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const out: PendingShot[] = [];
    for (const e of parsed) {
      if (!e || typeof e !== 'object') continue;
      const rounds = Math.max(0, Math.trunc(Number(e.rounds)));
      if (typeof e.magazine_id !== 'string' || !e.magazine_id || rounds <= 0) continue;
      out.push({
        id: typeof e.id === 'string' && e.id ? e.id : makeId(),
        date: typeof e.date === 'string' ? e.date : '',
        magazine_id: e.magazine_id,
        rounds,
        venue_id: typeof e.venue_id === 'string' ? e.venue_id : null,
        session_id: typeof e.session_id === 'string' && e.session_id ? e.session_id : undefined,
      });
    }
    return out;
  } catch {
    return [];
  }
}

function write(list: PendingShot[]): PendingShot[] {
  try {
    localStorage.setItem(KEY, JSON.stringify(list));
  } catch {
    /* ignore — private mode / storage disabled */
  }
  return list;
}

export function enqueuePendingShot(shot: Omit<PendingShot, 'id'>): PendingShot[] {
  return write([...readPendingShots(), { ...shot, id: makeId() }]);
}

export function removePendingShot(id: string): PendingShot[] {
  return write(readPendingShots().filter((e) => e.id !== id));
}

/** Entries for one group — drives the card's "needs attention" badge. */
export function pendingShotsForMag(magazineId: string): PendingShot[] {
  return readPendingShots().filter((e) => e.magazine_id === magazineId);
}
